import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { AuthService } from '../authentication/auth.service';
import { catchError, filter, from, map, Observable, take, throwError, timeout } from 'rxjs';
import { ID_TOKEN_KEY } from 'src/app/shared/constants/auth_constants';
import { LoaderService } from 'src/app/common/services/loader/loader.service';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { switchMap } from 'rxjs';



@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  
  // Timeout configuration
  private readonly REFRESH_TIMEOUT_MS = 10000; // 10 second timeout for token refresh
  private readonly REQUEST_TIMEOUT_MS = 30000; // 30 second timeout for HTTP requests

  constructor(private authService: AuthService,
    private loader: LoaderService,
    private toast: NgToastTranslocoService
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

    const requestUrl = request.url;

    this.loader.setLoading(true, requestUrl, false);
    let jsonRequest = requestUrl.includes('.json');

    // Check if the token is valid
    if (!jsonRequest && this.authService.isValidToken(ID_TOKEN_KEY)) {
      return from(this.authService.isTokenNearingExpiry()).pipe(
        timeout(this.REFRESH_TIMEOUT_MS), // Add timeout protection
        switchMap((isExpiring) => {
          if (isExpiring) {
            console.log('[Interceptor] Token expiring soon - refreshing proactively');
            return from(this.authService.refreshTokens()).pipe(
              timeout(this.REFRESH_TIMEOUT_MS), // Add timeout protection to refresh
              switchMap(newToken => {
                const authReq = this.addTokenHeader(request, newToken);
                return this.processRequest(authReq, next, request.url, { isRetryAfterTokenRefresh: true });
              }),
              catchError(error => {
                console.error('[Interceptor] Error during proactive token refresh:', error);
                this.loader.setLoading(false, request.url, true);
                
                // If refresh fails, logout user
                if (error.name === 'TimeoutError') {
                  this.toast.toastError('MODULES.COMMON.MESSAGES.TOKEN_REFRESH_TIMEOUT');
                } else {
                  this.toast.toastError('MODULES.COMMON.MESSAGES.SESSION_ENDED_MESSAGE');
                }
                this.authService.logoutSignedInUser();
                return throwError(() => error);
              })
            );
          } else {
            const token = this.authService.getAuthToken(ID_TOKEN_KEY);
            request = this.addTokenHeader(request, token);
            return this.processRequest(request, next, request.url, { isRetryAfterTokenRefresh: false });
          }
        }),
        catchError(error => {
          this.loader.setLoading(false, request.url, true);
          if (error.name === 'TimeoutError') {
            console.error('[Interceptor] Token validation timeout');
            this.toast.toastError('MODULES.COMMON.MESSAGES.TOKEN_REFRESH_TIMEOUT');
          }
          return throwError(() => error);
        })
      );
    }

    return next.handle(request)
      .pipe(
        timeout(this.REQUEST_TIMEOUT_MS), // Add timeout to regular requests
        catchError((error) => {
          this.loader.setLoading(false, request.url, true);
          
          if (error.name === 'TimeoutError') {
            console.error('[Interceptor] Request timeout');
            this.toast.toastError('MODULES.COMMON.MESSAGES.REQUEST_TIMEOUT');
            return throwError(() => error);
          }
          
          if (error && error instanceof HttpErrorResponse && error?.status === 401) {
            return this.handle401Error(request, next);
          }
          return throwError(() => error);
        })
      )
      .pipe(map<HttpEvent<any>, any>((evt: HttpEvent<any>) => {
        if (evt instanceof HttpResponse) {
          this.loader.setLoading(false, request.url, false);
        }
        return evt;
      }));
  }

  private processRequest(request: HttpRequest<any>, next: HttpHandler, requestUrl: string, { isRetryAfterTokenRefresh = false }: { isRetryAfterTokenRefresh?: boolean }): Observable<HttpEvent<any>> {

    return next.handle(request).pipe(
      timeout(this.REQUEST_TIMEOUT_MS), // Add timeout to processed requests
      map<HttpEvent<any>, any>((evt: HttpEvent<any>) => {
        if (evt instanceof HttpResponse) {
          this.loader.setLoading(false, requestUrl, false);
        }
        return evt;
      }),
      catchError(error => {
        this.loader.setLoading(false, requestUrl, true);
        
        // Handle timeout errors
        if (error.name === 'TimeoutError') {
          console.error('[Interceptor] Processed request timeout');
          this.toast.toastError('MODULES.COMMON.MESSAGES.REQUEST_TIMEOUT');
          return throwError(() => error);
        }
        
        if (error && error instanceof HttpErrorResponse && error?.status === 401) {
          if (isRetryAfterTokenRefresh) {
            console.error('[Interceptor] Still 401 after token refresh - logging out');
            this.toast.toastError('MODULES.COMMON.MESSAGES.SESSION_ENDED_MESSAGE');
            this.authService.logoutSignedInUser();
          } else {
            return this.handle401Error(request, next);
          }          
        }
        return throwError(() => error);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<unknown>, token: string) {
    request = request.clone({
      setHeaders: {
        Authorization: `${token}`,
        Accept: 'application/vnd.playerinfo.api+json; version=1.0'
      }
    });
    return request;
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      // Call refresh token method with timeout protection
      return from(this.authService.refreshTokens()).pipe(
        timeout(this.REFRESH_TIMEOUT_MS), // Add timeout protection
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          const authReq = this.addTokenHeader(request, newToken);

          // Retry the failed request with new token
          return this.processRequest(authReq, next, request.url, { isRetryAfterTokenRefresh: true });
        }),
        catchError(err => {
          this.isRefreshing = false;
          
          console.error('[Interceptor] Error during 401 token refresh:', err);
          
          if (err.name === 'TimeoutError') {
            this.toast.toastError('MODULES.COMMON.MESSAGES.TOKEN_REFRESH_TIMEOUT');
          } else if (err && err instanceof HttpErrorResponse && err?.status === 401) {
            this.toast.toastError('MODULES.COMMON.MESSAGES.SESSION_ENDED_MESSAGE');
            this.authService.logoutSignedInUser();
          } else {
            return throwError(() => err);
          }
        })
      );
    } else {
      // Wait for token refresh to complete
      return this.authService.tokenRefreshed$.pipe(
        filter(token => token !== null),
        take(1),
        timeout(this.REFRESH_TIMEOUT_MS), // Add timeout protection
        switchMap((token) => {
          const authReq = this.addTokenHeader(request, token!);
          return this.processRequest(authReq, next, request.url, { isRetryAfterTokenRefresh: true });
        }),
        catchError(err => {
          console.error('[Interceptor] Error waiting for token refresh:', err);
          return throwError(() => err);
        })
      );
    }
  }
}

